import React, { useRef, useState } from 'react';
import { formatPrice } from '../../../services/formatPrice';
import Button from '../../Button';
import { TextInput } from '../../Form';
import useCheckoutFormHelpers from '../../../hooks/useCheckoutFormHelpers';
import useCheckoutState from '../../../hooks/useCheckoutState';
import ErrorMessage from './ErrorMessage';
import { FormFieldItem } from './FormFieldItem';
import Label from './Label';
import useCheckoutHelpers from '../../../hooks/useCheckoutHelpers';
import Spinner from '../../Spinner';
import useCartCost from '../../../hooks/useCartCost';

function CouponField() {
  const couponCodeField = useRef();
  const { checkout, form, coupon, setCoupon, product } = useCheckoutState();
  const { setFormStateByKey } = useCheckoutFormHelpers();
  const { validateCoupon } = useCheckoutHelpers();
  const { couponDiscountAmount, planIterations, currency } = useCartCost();

  const [isValidatingCoupon, setIsValidatingCoupon] = useState('');
  const [couponError, setCouponError] = useState('');

  async function handleValidateCoupon() {
    try {
      setIsValidatingCoupon(true);
      setCouponError('');

      await validateCoupon(form.discountCode);

      setFormStateByKey('discountCode', '');
      setIsValidatingCoupon(false);
    } catch (error) {
      setIsValidatingCoupon(false);
      couponCodeField.current?.focus();
      setCouponError(error.message);
    }
  }

  return (
    <>
      <FormFieldItem className="cp_form-item">
        <Label htmlFor="discountCode" className="cp_form-label">
          {checkout.discountCodeText}
        </Label>

        {!coupon && (
          <>
            <div className="cp_coupon-form relative">
              <TextInput
                innerRef={couponCodeField}
                className="cp_form-textinput pr-16"
                id="discountCode"
                name="discountCode"
                type="text"
                placeholder={checkout.discountCodePlaceholderText}
                value={form.discountCode}
                onChange={e => setFormStateByKey('discountCode', e.target.value)}
                onKeyPress={e => {
                  if (e.which === 13 && form.discountCode) {
                    e.preventDefault();
                    handleValidateCoupon();
                  }
                }}
              />

              <Button
                appearance="bare"
                type="button"
                disabled={!form.discountCode || isValidatingCoupon}
                onClick={handleValidateCoupon}
                className="cp_button cp_coupon-apply-button absolute right-1 top-0 h-10 w-16 justify-center"
              >
                {isValidatingCoupon && <Spinner width="32px" height="32px" />}
                {!isValidatingCoupon && checkout.discountCodeApplyText}
              </Button>
            </div>
          </>
        )}

        {couponError && couponError.length > 0 && (
          <ErrorMessage variant="error" className="cp_error cp_coupon-error">
            {couponError}
          </ErrorMessage>
        )}

        {coupon && (
          <div className="cp_coupon flex gap-x-2 items-baseline mt-2 text-gray-800">
            <div className="cp_coupon-code">
              <div className="cp_coupon-code-text-wrapper bg-gray-100 rounded flex items-center justify-start py-1 gap-x-1">
                <span className="cp_coupon-code-text text-sm font-semibold uppercase pl-2">
                  {coupon.code}
                </span>

                <button
                  onClick={() => setCoupon(null)}
                  type="button"
                  aria-label="Remove discount code"
                  className="cp_coupon-remove fill-gray-300 hover:fill-gray-500 transition-colors mt-px px-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="24px"
                    height="24px"
                  >
                    <path d="M16.24 14.83a1 1 0 0 1-1.41 1.41L12 13.41l-2.83 2.83a1 1 0 0 1-1.41-1.41L10.59 12 7.76 9.17a1 1 0 0 1 1.41-1.41L12 10.59l2.83-2.83a1 1 0 0 1 1.41 1.41L13.41 12l2.83 2.83z" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="cp_coupon-discount text-black font-medium text-sm">
              -
              {formatPrice({
                amount: couponDiscountAmount,
                currency: currency,
                locale: checkout.locale,
                style: 'currency',
              })}
              {product.type === 'subscription' && !planIterations && (
                <>
                  {coupon.duration === 'once' && ' (once)'}
                  {coupon.duration === 'forever' && ' (forever)'}
                  {coupon.duration === 'repeating' && ` (${coupon.durationInMonths} months)`}
                </>
              )}
            </div>
          </div>
        )}
      </FormFieldItem>
    </>
  );
}

export default CouponField;
